// @ts-strict-ignore
import _ from 'lodash';
import moment from 'moment-timezone';
import { parseDuration } from '@/datetime/dateTime.utilities';
import { InitializeMode, PersistenceLevel, Store } from '@/core/flux.service';
import { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import {
  AUTO_UPDATE,
  DEFAULT_DISPLAY_RANGE_DURATION_DAYS,
  DEFAULT_INVESTIGATE_RANGE_DURATION_DAYS,
  DEFAULT_VANTAGE_DISPLAY_RANGE_DURATION_DAYS,
} from '@/trendData/trendData.constants';
import { sqWorkbookStore } from '@/core/core.stores';

export interface RangeExport {
  start: moment.Moment;
  end: moment.Moment;
  duration: moment.Duration;
}

export type AutoUpdateMode = 'AUTO' | 'MANUAL' | 'OFF' | 'OVERRIDE_VIA_DISPLAY_RANGE_QUERY_PARAMS_TO_SHOW_NOW_CURSOR';

export interface AutoUpdateData {
  mode: AutoUpdateMode;
  offset: number;
  interval: number;
  manualInterval: ValueWithUnitsItem;
  autoInterval: number;
  displayPixels: number;
  now: number;
}

export class DurationStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqDurationStore';

  initialize(initializeMode: InitializeMode) {
    moment.locale(window.navigator.language);
    const now = moment.utc().valueOf();
    const saveState = this.state && initializeMode !== 'FORCE';

    this.state = this.immutable(
      {
        // Avoid clearing state that is not dehydrated when the store is re-initialized. Async calls will repopulate
        // these properties as needed
        autoUpdate: {
          mode: sqWorkbookStore.isVantage ? AUTO_UPDATE.MODES.MANUAL : AUTO_UPDATE.MODES.OFF,
          offset: 0,
          interval: saveState ? this.state.get(['autoUpdate', 'interval']) : AUTO_UPDATE.MIN_INTERVAL,
          manualInterval: AUTO_UPDATE.DEFAULT_INTERVAL,
          autoInterval: saveState ? this.state.get(['autoUpdate', 'autoInterval']) : 0,
          displayPixels: saveState ? this.state.get(['autoUpdate', 'displayPixels']) : 0,
          now,
        } as AutoUpdateData,

        displayRangeOriginal: undefined,
        investigateRangeOriginal: undefined,
        displayRange: {
          start: moment
            .utc(now)
            .subtract(
              sqWorkbookStore.isVantage
                ? DEFAULT_VANTAGE_DISPLAY_RANGE_DURATION_DAYS
                : DEFAULT_DISPLAY_RANGE_DURATION_DAYS,
              'days',
            )
            .valueOf(),
          end: now,
          momentRange: this.monkey(
            ['displayRange', 'start'],
            ['displayRange', 'end'],
            (start, end) => ({
              start: moment.utc(start),
              end: moment.utc(end),
              duration: moment.duration(end - start),
            }),
            { immutable: false },
          ),
        },
        investigateRange: {
          start: moment.utc(now).subtract(DEFAULT_INVESTIGATE_RANGE_DURATION_DAYS, 'days').valueOf(),
          end: now,
          momentRange: this.monkey(
            ['investigateRange', 'start'],
            ['investigateRange', 'end'],
            (start, end) => ({
              start: moment.utc(start),
              end: moment.utc(end),
              duration: moment.duration(end - start),
            }),
            { immutable: false },
          ),
        },
      },
      {
        asynchronous: false, // This store emits instantly to ensure the chart scrolls smoothly
      },
    );
  }

  get autoUpdate(): AutoUpdateData {
    return this.state.get('autoUpdate');
  }

  get displayRange(): RangeExport {
    return this.state.get('displayRange', 'momentRange');
  }

  get investigateRange(): RangeExport {
    return this.state.get('investigateRange', 'momentRange');
  }

  get displayPixels(): number {
    return this.state.get(['autoUpdate', 'displayPixels']);
  }

  /*********************************************** Auto Update ***************************************************/

  /**
   * Helper function that computes autoInterval and interval values.
   */
  private autoUpdateComputeIntervals() {
    let interval, autoInterval;
    const mode = this.state.get(['autoUpdate', 'mode']);
    const displayPixels = this.state.get(['autoUpdate', 'displayPixels']);
    const manualInterval = this.state.get(['autoUpdate', 'manualInterval']);
    const displayDuration = this.state.get(['displayRange', 'momentRange', 'duration']);

    // Compute the auto interval based on the display range duration and number of display pixels
    if (displayPixels && displayDuration.valueOf()) {
      autoInterval = Math.round(Math.max(AUTO_UPDATE.MIN_INTERVAL, displayDuration.valueOf() / displayPixels));
    } else {
      autoInterval = parseDuration(AUTO_UPDATE.DEFAULT_INTERVAL.value + AUTO_UPDATE.DEFAULT_INTERVAL.units).valueOf();
    }

    this.state.set(['autoUpdate', 'autoInterval'], autoInterval);

    if (mode === AUTO_UPDATE.MODES.AUTO) {
      interval = autoInterval;
    } else if (mode === AUTO_UPDATE.MODES.MANUAL) {
      interval = parseDuration(manualInterval.value + manualInterval.units).valueOf();
    } else {
      interval = AUTO_UPDATE.MIN_INTERVAL;
    }

    this.state.set(['autoUpdate', 'interval'], interval);
  }

  /**
   * Auto-update should not result in new worksteps, but it changes the same fields in the store that a user would
   * change via the UI so this method prevents new worksteps by saving off the original range when auto-update is
   * changing it. The original range is then used when dehydrating the store.
   *
   * @param isAutoUpdate - True if this originates from auto-update
   * @param isDisplayRange True if the range is the display range, false if it is the investigate range
   */
  private setRangeCopyForAutoUpdate(isAutoUpdate: boolean, isDisplayRange: boolean) {
    const key = isDisplayRange ? 'displayRange' : 'investigateRange';
    const originalKey = isDisplayRange ? 'displayRangeOriginal' : 'investigateRangeOriginal';

    if (isAutoUpdate && !this.state.get(originalKey)) {
      this.state.set(originalKey, this.state.set(originalKey, _.pick(this.state.get(key), ['start', 'end'])));
    }
    if (!isAutoUpdate) {
      this.state.unset(originalKey);
    }
  }

  /********************************************* Display Range *************************************************/

  /**
   * Updates display range start and end times. Duration is automatically updated to reflect the new range.
   *
   * @param payload - Object container for arguments
   * @param payload.start - New display range start time
   * @param payload.end - New display range end time
   * @param payload.isAutoUpdate - True if this originates from auto-update
   */
  private updateDisplayRangeTimes(payload: { start: number; end: number; isAutoUpdate: boolean }) {
    const endMoment = moment.utc(payload.end);
    const startMoment = moment.utc(payload.start);
    this.setRangeCopyForAutoUpdate(payload.isAutoUpdate, true);

    this.state.merge('displayRange', {
      start: startMoment.valueOf(),
      end: endMoment.valueOf(),
    });
    this.autoUpdateComputeIntervals();
  }

  /******************************************** Investigate Range ************************************************/

  /**
   * Updates investigate range start and end times. Duration is automatically updated to reflect the new range.
   *
   * @param payload - Object container for arguments
   * @param payload.start - New investigate range start time
   * @param payload.end - New investigate range end time
   * @param payload.isAutoUpdate - True if this originates from auto-update
   */
  private updateInvestigateRangeTimes(payload: { start: number; end: number; isAutoUpdate: boolean }) {
    const endMoment = moment.utc(payload.end);
    const startMoment = moment.utc(payload.start);
    this.setRangeCopyForAutoUpdate(payload.isAutoUpdate, false);

    this.state.merge('investigateRange', {
      start: startMoment.valueOf(),
      end: endMoment.valueOf(),
    });
  }

  protected readonly handlers = {
    AUTO_UPDATE_SET_NOW: ({ now }) => {
      this.state.set(['autoUpdate', 'now'], now);
    },
    AUTO_UPDATE_SET_MODE: ({ mode }) => {
      this.state.set(['autoUpdate', 'mode'], mode);
      this.autoUpdateComputeIntervals();
    },
    AUTO_UPDATE_COMPUTE_OFFSET: () => {
      const offset = this.state.get('displayRange', 'end') - this.state.get('autoUpdate', 'now');
      this.state.set(['autoUpdate', 'offset'], offset);
    },
    AUTO_UPDATE_CLEAR_OFFSET: () => {
      this.state.set(['autoUpdate', 'offset'], 0);
    },
    AUTO_UPDATE_SET_MANUAL_INTERVAL: ({ manualInterval }) => {
      this.state.set(['autoUpdate', 'manualInterval'], manualInterval);
      this.autoUpdateComputeIntervals();
    },
    AUTO_UPDATE_SET_DISPLAY_PIXELS: ({ displayPixels }) => {
      this.state.set(['autoUpdate', 'displayPixels'], displayPixels);
      this.autoUpdateComputeIntervals();
    },
    UPDATE_DISPLAY_RANGE_TIMES: this.updateDisplayRangeTimes,
    UPDATE_INVESTIGATE_RANGE_TIMES: this.updateInvestigateRangeTimes,
  };

  dehydrate() {
    const state = _.omit(this.state.serialize(), ['displayRangeOriginal', 'investigateRangeOriginal']);
    state.autoUpdate = _.omit(state.autoUpdate, ['now', 'interval', 'autoInterval', 'displayPixels']);
    if (state.autoUpdate.mode !== AUTO_UPDATE.MODES.OFF) {
      if (this.state.get('displayRangeOriginal')) {
        state.displayRange = this.state.get('displayRangeOriginal');
      }
      if (this.state.get('investigateRangeOriginal')) {
        state.investigateRange = this.state.get('investigateRangeOriginal');
      }
    }

    return state;
  }

  rehydrate(newState) {
    this.state.merge('autoUpdate', newState.autoUpdate);
    this.autoUpdateComputeIntervals();

    if (newState.investigateRange) {
      this.updateInvestigateRangeTimes({
        end: newState.investigateRange.end,
        start: newState.investigateRange.start,
        isAutoUpdate: false,
      });
    }

    if (newState.displayRange) {
      this.updateDisplayRangeTimes({
        end: newState.displayRange.end,
        start: newState.displayRange.start,
        isAutoUpdate: false,
      });
    }
  }
}
